import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { Person, PersonBreezeModel } from "@common/ADAPT.Common.Model/person/person";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { UserService } from "@common/user/user.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { PeerCatchupService } from "@org-common/lib/peer-catchup/peer-catchup.service";
import { PeerCatchupAuthService } from "@org-common/lib/peer-catchup/peer-catchup-auth.service";
import { PeerCatchupUiService } from "@org-common/lib/peer-catchup/peer-catchup-ui.service";
import { BehaviorSubject, combineLatest, forkJoin, Subject } from "rxjs";
import { debounceTime, switchMap, take } from "rxjs/operators";

@Component({
    selector: "adapt-display-missing-catchup",
    templateUrl: "./display-missing-catchup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class DisplayMissingCatchupComponent extends BaseComponent {
    @Input() public set person1Id(personId: number) {
        this.person1Id$.next(personId);
    }

    @Input() public set person2Id(personId: number) {
        this.person2Id$.next(personId);
    }

    @Input() public set teamId(teamId: number) {
        this.teamId$.next(teamId);
    }

    public person1?: Person;
    public person2?: Person;
    public culturalActivity?: CulturalActivity;
    public canEditPersonalCatchups = false;

    private person1Id$ = new Subject<number>();
    private person2Id$ = new Subject<number>();
    private teamId$ = new BehaviorSubject<number | undefined>(undefined);

    public constructor(
        commonDataService: CommonDataService,
        peerCatchupService: PeerCatchupService,
        peerCatchupAuthService: PeerCatchupAuthService,
        private peerCatchupUiService: PeerCatchupUiService,
        private userService: UserService,
    ) {
        super();

        peerCatchupAuthService.canEditPersonalCatchups.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((canEdit) => this.canEditPersonalCatchups = canEdit);

        combineLatest([this.person1Id$, this.person2Id$, this.teamId$]).pipe(
            debounceTime(100),
            switchMap(([person1Id, person2Id, teamId]) => forkJoin([
                commonDataService.getById(PersonBreezeModel, person1Id),
                commonDataService.getById(PersonBreezeModel, person2Id),
                peerCatchupService.getNextCatchupActivity(person1Id, person2Id, teamId),
            ])),
            take(1),
            this.takeUntilDestroyed(),
        ).subscribe(([person1, person2, culturalActivity]) => {
            this.person1 = person1;
            this.person2 = person2;
            this.culturalActivity = culturalActivity;
        });
    }

    public get bothPeopleActive() {
        return (this.person1?.isActive() && this.person2?.isActive()) ?? false;
    }

    public get isInvolvedPerson() {
        // cant use catchup extensions isInvolvedPerson here as no catchup entity
        const personId = this.userService.getCurrentPersonId();
        return this.person1?.personId === personId
            || this.person2?.personId === personId;
    }

    public get isScheduled() {
        return !!this.culturalActivity;
    }

    public recordNewCatchup() {
        return this.peerCatchupUiService
            .recordNewCatchup(this.person1!.personId, this.person2!.personId, this.teamId$.value)
            .subscribe();
    }

    public scheduleCatchup() {
        if (this.culturalActivity) {
            return this.peerCatchupUiService
                .rescheduleCatchupActivity(this.culturalActivity)
                .subscribe();
        }

        return this.peerCatchupUiService
            .scheduleCatchup(this.person1!.personId, this.person2!.personId, this.teamId$.value)
            .subscribe();
    }
}
